import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import ArtistDiscussions from "@/components/community/ArtistDiscussions";
import CampfireRelated from "@/components/CampfireRelated";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Music, Check, Plus, ExternalLink, Bell, ArrowLeft, Sparkles, Loader2, Globe } from "lucide-react";

const SOCIALS = [["spotify", "Spotify"], ["bandcamp", "Bandcamp"], ["youtube", "YouTube"], ["instagram", "Instagram"], ["facebook", "Facebook"]];

export default function ArtistDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const [official, setOfficial] = useState("");
  const [savingOfficial, setSavingOfficial] = useState(false);

  const { data: artist, isLoading, refetch: refetchArtist } = useQuery({ queryKey: ["artist", slug], queryFn: async () => (await api.get(`/artists/${slug}`)).data });
  const { data: myArtists = [], refetch } = useQuery({ queryKey: ["myArtists"], queryFn: async () => (await api.get("/me/artists")).data, enabled: !!user });

  useEffect(() => { if (artist) setFollowing(myArtists.includes(artist.name)); }, [myArtists, artist]);
  useEffect(() => { if (artist) setOfficial(artist.official_website || ""); }, [artist]);

  const saveOfficial = async () => {
    setSavingOfficial(true);
    try { await api.put(`/admin/watchlist/${slug}`, { official_website: official.trim() }); toast.success("Site officiel enregistré"); refetchArtist(); }
    catch { toast.error(t("aw_error")); } finally { setSavingOfficial(false); }
  };
  const enrichFromSite = async () => {
    if (!official.trim()) return;
    setEnriching(true);
    try { await api.post(`/admin/watchlist/${slug}/enrich?source_url=${encodeURIComponent(official.trim())}`); toast.success(t("artist_enriched")); refetchArtist(); }
    catch { toast.error(t("aw_error")); } finally { setEnriching(false); }
  };

  const enrich = async () => {
    setEnriching(true);
    try { await api.post(`/admin/watchlist/${slug}/enrich`); toast.success(t("artist_enriched")); refetchArtist(); }
    catch { toast.error(t("aw_error")); } finally { setEnriching(false); }
  };

  const toggleFollow = async () => {
    if (!user) { toast.error(t("artist_login_follow")); return; }
    try {
      if (following) { await api.delete(`/me/artists/${encodeURIComponent(artist.name)}`); setFollowing(false); }
      else { await api.post("/me/artists", { artist: artist.name }); setFollowing(true); toast.success(t("artist_followed")); }
      refetch();
    } catch { toast.error(t("aw_error")); }
  };

  if (isLoading) return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!artist) return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-stone-400">404</div>;

  const concerts = artist.upcoming_concerts || [];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "MusicGroup", name: artist.name,
      ...(artist.genres?.length ? { genre: artist.genres } : {}),
      ...(artist.official_website ? { url: artist.official_website } : {}) },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Artistes", item: `${origin}/artistes` },
      { "@type": "ListItem", position: 2, name: artist.name, item: `${origin}/artistes/${slug}` },
    ] },
  ];

  return (
    <>
      <Seo title={artist.name} description={t("artist_seo_desc").replace("{name}", artist.name)} jsonLd={jsonLd} canonicalPath={`/artistes/${slug}`} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <Link to="/artistes" data-testid="artist-back" className="inline-flex items-center gap-1.5 text-stone-400 hover:text-indigo-300 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> {t("artists_title")}</Link>

        <div className="flex flex-col sm:flex-row sm:items-center gap-6 pb-8 mb-10 border-b border-indigo-900/30">
          {artist.image && !imgBroken
            ? <img src={artist.image} alt={artist.name} data-testid="artist-photo" onError={() => setImgBroken(true)} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border border-indigo-800/40 shrink-0" />
            : <span className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-900/50 to-[#121418] border border-indigo-800/40 flex items-center justify-center shrink-0"><Music className="w-9 h-9 text-indigo-300" /></span>}
          <div className="flex-1 min-w-0">
            <h1 className="font-serif text-4xl sm:text-5xl text-amber-100 text-glow leading-tight">{artist.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-stone-400">
              {artist.genres?.length > 0 && <span className="font-mono text-indigo-300/80">{artist.genres.join(" · ")}</span>}
              {artist.country && <span className="inline-flex items-center gap-1 font-mono text-xs"><Globe className="w-3.5 h-3.5" /> {artist.country}</span>}
              <span className="font-mono text-xs">{artist.follower_count || 0} {t("artist_followers")}</span>
            </div>
          </div>
          <button data-testid="artist-follow-btn" onClick={toggleFollow}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold text-sm border transition-all shrink-0 ${following ? "bg-indigo-600 text-white border-indigo-400" : "bg-[#181A20] text-stone-200 border-[#272A34] hover:border-indigo-500/50"}`}>
            {following ? <><Check className="w-4 h-4" /> {t("artist_following")}</> : <><Plus className="w-4 h-4" /> {t("artist_follow")}</>}
          </button>
        </div>

        {artist.bio && <p className="text-stone-300 leading-relaxed max-w-3xl mb-8" data-testid="artist-bio">{artist.bio}</p>}

        {user?.role === "admin" && (
          <div className="mb-8 rounded-lg border border-indigo-800/40 bg-[#121418] p-4 space-y-3" data-testid="artist-admin-panel">
            <div className="font-mono text-[11px] uppercase tracking-wider text-indigo-300/80">Admin — Site officiel & enrichissement</div>
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid="artist-official-input" value={official} onChange={(e) => setOfficial(e.target.value)} placeholder="https://site-officiel-du-groupe.com" className="flex-1 min-w-[220px] bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 outline-none focus:border-indigo-500/50" />
              <button data-testid="artist-official-save" onClick={saveOfficial} disabled={savingOfficial} className="inline-flex items-center gap-1.5 text-xs bg-[#181A20] border border-[#272A34] hover:border-indigo-500/40 text-stone-200 px-3 py-2 rounded-md disabled:opacity-60">{savingOfficial ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Enregistrer</button>
              <button data-testid="artist-enrich-site" onClick={enrichFromSite} disabled={enriching || !official.trim()} className="inline-flex items-center gap-1.5 text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-3 py-2 rounded-md disabled:opacity-60">{enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Enrichir depuis le site</button>
            </div>
            <button data-testid="artist-enrich-btn" onClick={enrich} disabled={enriching} className="inline-flex items-center gap-2 text-xs text-stone-400 hover:text-indigo-300 disabled:opacity-60">
              {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {enriching ? t("artist_enriching") : "Enrichir depuis Wikipedia"}
            </button>
            <p className="text-[11px] text-stone-500">Le site officiel est utilisé en priorité pour récupérer les liens Spotify / Bandcamp / YouTube (au lieu de Wikipedia seul).</p>
          </div>
        )}

        {(artist.official_website || artist.official_tour_url || (artist.links && Object.keys(artist.links).length > 0)) && (
          <div className="flex flex-wrap gap-3 mb-10">
            {artist.official_website && <a href={artist.official_website} target="_blank" rel="noopener noreferrer" data-testid="artist-website" className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-indigo-500/40"><ExternalLink className="w-4 h-4" /> {t("artist_website")}</a>}
            {artist.official_tour_url && <a href={artist.official_tour_url} target="_blank" rel="noopener noreferrer" data-testid="artist-tour" className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-indigo-500/40"><ExternalLink className="w-4 h-4" /> {t("artist_tour")}</a>}
            {SOCIALS.map(([k, label]) => artist.links?.[k] && (
              <a key={k} href={artist.links[k]} target="_blank" rel="noopener noreferrer" data-testid={`artist-link-${k}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-indigo-500/40"><ExternalLink className="w-4 h-4" /> {label}</a>
            ))}
          </div>
        )}

        <h2 className="font-serif text-2xl text-stone-100 mb-6 border-b border-amber-900/30 pb-4">{t("artist_concerts")} ({concerts.length})</h2>
        {concerts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#272A34] bg-[#121418] p-10 text-center" data-testid="artist-no-concerts">
            <Bell className="w-8 h-8 text-indigo-400/70 mx-auto mb-3" />
            <p className="text-stone-400 max-w-md mx-auto mb-5">{t("artist_no_concerts")}</p>
            <button data-testid="artist-follow-cta" onClick={toggleFollow} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-6 py-2.5 rounded-md transition-all">
              {following ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {following ? t("artist_following") : t("artist_follow")}
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="artist-concerts">
            {concerts.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}
          </div>
        )}

        <ArtistDiscussions artistSlug={slug} />
      </div>

      <CampfireRelated type="artist" slug={slug} />
    </>
  );
}
